import React, { useState, useRef, useEffect } from 'react';

interface QuickQuestionsTooltipProps {
  questions: string[];
  onQuestionClick: (question: string) => void;
  disabled?: boolean;
}

const QuickQuestionsTooltip: React.FC<QuickQuestionsTooltipProps> = ({
  questions,
  onQuestionClick,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout>();
  const showTimeoutRef = useRef<NodeJS.Timeout>();

  // Effects must be called before any early returns
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
    };
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Validate questions are strings
  const validQuestions = questions?.filter(q => typeof q === 'string' && q.trim().length > 0) || [];
  
  // Determine if lamp should be interactive
  const hasQuestions = validQuestions.length > 0;
  const isLampDisabled = disabled || !hasQuestions;

  const clearTimeouts = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }
  };

  const handleContainerMouseEnter = () => {
    clearTimeouts();
    
    // Show tooltip with small delay for better UX
    showTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 150);
  };

  const handleContainerMouseLeave = () => {
    clearTimeouts();
    
    // Hide tooltip with delay to allow moving between lamp and tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  const handleClick = () => {
    clearTimeouts();
    setIsOpen(!isOpen);
  };

  const handleQuestionClick = (question: string) => {
    onQuestionClick(question);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={tooltipRef}>
      {/* Lamp Icon Button - Always visible */}
      <button
        onClick={handleClick}
        onMouseEnter={handleContainerMouseEnter}
        onMouseLeave={handleContainerMouseLeave}
        className={`
          p-2 rounded-lg transition-all duration-200
          ${isLampDisabled 
            ? 'text-gray-300 cursor-not-allowed' 
            : isOpen 
              ? 'bg-blue-100 text-blue-600' 
              : 'text-gray-400 hover:text-blue-500 hover:bg-gray-50'
          }
        `}
        title={
          isLampDisabled 
            ? "No quick questions available" 
            : "Quick questions and suggestions"
        }
        aria-label={
          isLampDisabled 
            ? "No quick questions available" 
            : "Show quick questions"
        }
        disabled={isLampDisabled}
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5"
        >
          {/* Light bulb icon */}
          <path 
            d="M9 21h6m-6 0v-1a1 1 0 011-1h4a1 1 0 011 1v1m-6 0H8m8 0h1M12 3a6 6 0 00-6 6c0 2.7 1.5 3.5 2 4.5V16h8v-2.5c.5-1 2-1.8 2-4.5a6 6 0 00-6-6z" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Tooltip Content - Only show if lamp is enabled and has questions */}
      {isOpen && !isLampDisabled && hasQuestions && (
        <div 
          className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50"
          onMouseEnter={handleContainerMouseEnter}
          onMouseLeave={handleContainerMouseLeave}
        >
          {/* Arrow pointing down */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-200 mt-px"></div>
          
          {/* Header */}
          <div className="flex items-center mb-3">
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-blue-500 mr-2"
            >
              <path 
                d="M9 21h6m-6 0v-1a1 1 0 011-1h4a1 1 0 011 1v1m-6 0H8m8 0h1M12 3a6 6 0 00-6 6c0 2.7 1.5 3.5 2 4.5V16h8v-2.5c.5-1 2-1.8 2-4.5a6 6 0 00-6-6z" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">Quick Questions</span>
          </div>

          {/* Questions */}
          <div className="space-y-2">
            {validQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleQuestionClick(question)}
                className="
                  w-full text-left px-3 py-2 rounded-md text-sm text-gray-700
                  hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150
                  border border-transparent hover:border-blue-200
                "
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Disabled state tooltip - Show brief explanation when disabled */}
      {isOpen && isLampDisabled && (
        <div 
          className="absolute bottom-full left-0 mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-2 z-50"
          onMouseEnter={handleContainerMouseEnter}
          onMouseLeave={handleContainerMouseLeave}
        >
          {/* Arrow pointing down */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          
          <span>
            {disabled 
              ? "Please wait for the AI to finish responding" 
              : "Quick questions will appear after the AI completes its response"
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default QuickQuestionsTooltip;
